import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { adminApi } from '@/lib/admin';
import { AdminShell } from '@/components/app/AdminShell';

export function AdminOverviewPage() {
  const { t } = useTranslation();
  const { data: m, isLoading } = useQuery({
    queryKey: ['admin', 'metrics'],
    queryFn: () => adminApi.metrics(),
    refetchInterval: 60_000,
  });
  const { data: queues } = useQuery({
    queryKey: ['admin', 'queues'],
    queryFn: () => adminApi.queueMetrics(),
    refetchInterval: 15_000,
  });

  return (
    <AdminShell
      title={t('admin.overview.title', { defaultValue: 'Operations overview' })}
      description={t('admin.overview.subtitle', { defaultValue: 'Cross-tenant health, usage, and trust signals.' }) as string}
    >
      {isLoading || !m ? (
        <div className="card-paper p-10 text-center text-ink-500 text-sm">{t('common.loading')}</div>
      ) : (
        <div className="space-y-7">
          <section>
            <h3 className="text-[11px] uppercase tracking-[0.16em] text-ink-500 font-semibold mb-3">
              {t('admin.overview.people', { defaultValue: 'People' })}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label={t('admin.metric.users') as string} value={m.users.total} hint={t('admin.metric.users_hint', { defaultValue: 'total accounts' }) as string} />
              <Stat label={t('admin.metric.verified') as string} value={m.users.verified} accent />
              <Stat label={t('admin.metric.suspended') as string} value={m.users.suspended} danger={m.users.suspended > 0} />
              <Stat label={t('admin.metric.admins') as string} value={m.users.siteAdmins} />
            </div>
          </section>

          <section>
            <h3 className="text-[11px] uppercase tracking-[0.16em] text-ink-500 font-semibold mb-3">
              {t('admin.overview.tenants', { defaultValue: 'Tenants' })}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label={t('admin.metric.companies') as string} value={m.companies.total} />
              {Object.entries(m.companies.byPlan).map(([plan, n]) => (
                <Stat key={plan} label={plan} value={n} />
              ))}
            </div>
          </section>

          <section>
            <h3 className="text-[11px] uppercase tracking-[0.16em] text-ink-500 font-semibold mb-3">
              {t('admin.overview.workload', { defaultValue: 'Workload' })}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label={t('admin.metric.docs') as string} value={m.documents.total} />
              <Stat label={t('admin.metric.indexed') as string} value={m.documents.indexed} accent />
              <Stat label={t('admin.metric.failed') as string} value={m.documents.failed} danger={m.documents.failed > 0} />
              <Stat label={t('admin.metric.ai_runs7d') as string} value={m.aiRuns7d.runs} hint={`$${(m.aiRuns7d.cents / 100).toFixed(2)} ${t('admin.metric.in_cost', { defaultValue: 'in cost' })}`} />
            </div>
          </section>

          {queues && (
            <section>
              <h3 className="text-[11px] uppercase tracking-[0.16em] text-ink-500 font-semibold mb-3">
                {t('admin.overview.queues', { defaultValue: 'Background queues' })}
              </h3>
              {!queues.enabled ? (
                <div className="card-paper px-4 py-3 text-[13px] text-ink-500">
                  {t('admin.overview.queues_disabled', { defaultValue: 'Queue service disabled — set REDIS_URL to enable.' })}
                </div>
              ) : Object.keys(queues.queues).length === 0 ? (
                <div className="card-paper px-4 py-3 text-[13px] text-ink-500">
                  {t('admin.overview.queues_idle', { defaultValue: 'No queues have produced jobs yet.' })}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(queues.queues).map(([name, c]) => (
                    <div key={name} className="card-paper p-4">
                      <div className="flex items-center justify-between">
                        <span className="display text-[14px] font-semibold text-ink-900 capitalize">{name}</span>
                        <span className="text-[10.5px] uppercase tracking-[0.12em] text-ink-400 font-semibold">queue</span>
                      </div>
                      <dl className="mt-3 grid grid-cols-5 gap-2 text-[11.5px]">
                        <QueueStat label={t('admin.queue.waiting') as string} value={c.waiting} />
                        <QueueStat label={t('admin.queue.active') as string} value={c.active} accent />
                        <QueueStat label={t('admin.queue.delayed') as string} value={c.delayed} />
                        <QueueStat label={t('admin.queue.completed') as string} value={c.completed} />
                        <QueueStat label={t('admin.queue.failed') as string} value={c.failed} danger={c.failed > 0} />
                      </dl>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      )}
    </AdminShell>
  );
}

function QueueStat({ label, value, accent, danger }: { label: string; value: number; accent?: boolean; danger?: boolean }) {
  return (
    <div>
      <dt className="text-ink-500 truncate">{label}</dt>
      <dd className={`num font-semibold tabular-nums ${danger ? 'text-warn-600' : accent ? 'text-accent-700' : 'text-ink-900'}`}>
        {value.toLocaleString()}
      </dd>
    </div>
  );
}

function Stat({
  label, value, hint, accent, danger,
}: { label: string; value: number; hint?: string; accent?: boolean; danger?: boolean }) {
  return (
    <div className="card-paper p-4">
      <div className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-semibold mb-1.5">{label}</div>
      <div className={`num text-2xl font-semibold tabular-nums ${danger ? 'text-warn-600' : accent ? 'gradient-text' : 'text-ink-900'}`}>
        {value.toLocaleString()}
      </div>
      {hint && <div className="text-[11px] text-ink-500 mt-1">{hint}</div>}
    </div>
  );
}
